import { Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import produce, { Draft } from "immer";
import { FC, Fragment, MouseEvent } from "react";
import { create } from "zustand";

/**
 * A single toast notification.
 */
export type ToastType = {
  /** Unique identifier */
  id: string;
  /** Timestamp to avoid collisions */
  timestamp: number;
  /** Rendered message text */
  message: string;
  /** Optional callback on dismiss */
  onDismiss?: (e: MouseEvent) => void;
  /** Whether toast is an error (red) or success (green) */
  error?: boolean;
};

/**
 * Zustand store that controls all toast notifications.
 */
export type ToastStore = {
  /** List of active toasts (max 5 displayed at once) */
  toasts: ToastType[];

  /** Add a toast to the queue */
  addToast: (toast: ToastType) => void;

  /** Remove a toast by ID */
  removeToast: (id: string) => void;
};

/**
 * Global toast store with:
 * - auto-removal after 2.5 seconds
 * - deduplication by id + timestamp
 * - max retained entries = 4
 */
export const useToast = create<ToastStore>((set) => ({
  toasts: [],

  addToast: (toast) => {
    set(
      produce((state: Draft<ToastStore>) => {
        // Remove duplicates
        const currentToasts = state.toasts.filter(
          ({ id }: ToastType) => id !== toast.id
        );

        // Limit to last 4
        currentToasts.length = Math.min(currentToasts.length, 4);

        // Add new toast to end
        state.toasts = [...currentToasts, toast];
      })
    );

    // Auto-hide after 2.5 seconds
    setTimeout(() => {
      set(
        produce((state: Draft<ToastStore>) => {
          // Ensure we only remove the exact toast
          state.toasts = state.toasts.filter(
            ({ id, timestamp }: ToastType) =>
              id !== toast.id || timestamp !== toast.timestamp
          );
        })
      );
    }, 2500);
  },

  removeToast: (id) => {
    set(
      produce((state: Draft<ToastStore>) => {
        state.toasts = state.toasts.filter(
          (toast: ToastType) => toast.id !== id
        );
      })
    );
  },
}));

/**
 * Toast renderer:
 * - Displays up to 5 stacked notifications
 * - Animates using Headless UI <Transition>
 */
export const Toast: FC = () => {
  const { toasts, removeToast } = useToast();

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-20 flex items-end px-4 py-6 sm:p-6"
    >
      <div className="flex w-full flex-col items-center space-y-4">
        {[...Array(5)].map((_, index) => {
          const toast = toasts[index];

          return (
            <Transition
              key={index}
              show={!!toast}
              as={Fragment}
              enter="transform ease-out duration-400 transition"
              enterFrom="translate-y-2 opacity-0"
              enterTo="translate-y-0 opacity-100"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <figure className="button-border pointer-events-auto flex w-full max-w-sm overflow-hidden rounded-lg border-gray-400/30 bg-white p-4 shadow-xl shadow-gray-500/10">
                {toast?.error ? (
                  <ExclamationCircleIcon
                    className="h-6 w-6 text-red-400"
                    aria-hidden="true"
                  />
                ) : (
                  <CheckCircleIcon
                    className="h-6 w-6 text-green-400"
                    aria-hidden="true"
                  />
                )}

                <p className="ml-3 w-0 flex-1 pt-0.5 text-sm font-medium text-gray-900">
                  {toast?.message}
                </p>

                <button
                  className="ml-4 inline-flex flex-shrink-0 rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={() => toast?.id && removeToast(toast.id)}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </figure>
            </Transition>
          );
        })}
      </div>
    </div>
  );
};

export default Toast;
